import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

/* Button */
type ButtonVariant = 'primary' | 'ghost' | 'quiet' | 'danger' | 'gold-outline'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
}

const buttonVariants: Record<ButtonVariant, string> = {
  primary: 'bg-gold text-navy-deep hover:bg-gold-soft border border-gold-deep',
  ghost: 'bg-transparent text-cream hover:bg-cream/5 border border-cream/20 hover:border-cream/40',
  quiet: 'bg-transparent text-cream/70 hover:text-cream hover:bg-cream/5 border-transparent',
  danger: 'bg-danger/15 text-danger border border-danger/40 hover:bg-danger/25',
  'gold-outline': 'bg-transparent text-gold-soft border border-gold/60 hover:bg-gold/10',
}

const buttonSizes: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, className, children, disabled, ...rest }, ref
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center gap-2 rounded-md font-medium transition-all duration-150',
        'tracking-wide font-body',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        buttonVariants[variant],
        buttonSizes[size],
        className,
      )}
      {...rest}
    >
      {loading && <span className="inline-block size-3 rounded-full border-2 border-current border-r-transparent animate-spin" />}
      {children}
    </button>
  )
})

/* Card */
export function Card({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'glass rounded-lg p-6',
        'transition-all duration-200',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  )
}

export function CardHeader({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex items-start justify-between gap-3 mb-4', className)} {...rest}>{children}</div>
}

export function CardTitle({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <h3 className={cn('font-heading text-xl text-cream tracking-tight', className)} {...rest}>{children}</h3>
}

export function CardDescription({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <p className={cn('text-sm text-cream/65 font-body', className)} {...rest}>{children}</p>
}

/* Badge */
type BadgeProps = HTMLAttributes<HTMLSpanElement> & {
  tone?: 'gold' | 'bronze' | 'cream' | 'success' | 'warning' | 'danger' | 'info'
  variant?: 'solid' | 'outline'
}

const badgeTones: Record<NonNullable<BadgeProps['tone']>, string> = {
  gold: 'bg-gold/15 text-gold-soft border-gold/30',
  bronze: 'bg-bronze/15 text-bronze border-bronze/30',
  cream: 'bg-cream/10 text-cream border-cream/20',
  success: 'bg-success/10 text-success border-success/30',
  warning: 'bg-warning/10 text-warning border-warning/30',
  danger: 'bg-danger/10 text-danger border-danger/30',
  info: 'bg-info/10 text-info border-info/30',
}

export function Badge({ tone = 'cream', className, children, ...rest }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5',
        'font-mono text-[10px] uppercase tracking-[0.12em] font-medium',
        badgeTones[tone],
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  )
}

/* Input */
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return (
      <input
        ref={ref}
        className={cn(
          'w-full rounded-md border border-cream/15 bg-navy/50 px-3 py-2',
          'text-cream placeholder:text-cream/40 font-body text-sm',
          'focus:outline-none focus:border-gold-soft focus:ring-1 focus:ring-gold-soft/40',
          'disabled:opacity-50',
          className,
        )}
        {...rest}
      />
    )
  }
)

/* Textarea */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        className={cn(
          'w-full rounded-md border border-cream/15 bg-navy/50 px-3 py-2',
          'text-cream placeholder:text-cream/40 font-body text-sm',
          'focus:outline-none focus:border-gold-soft focus:ring-1 focus:ring-gold-soft/40',
          'min-h-24 resize-y',
          className,
        )}
        {...rest}
      />
    )
  }
)

/* Select */
export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <select
        ref={ref}
        className={cn(
          'rounded-md border border-cream/15 bg-navy/50 px-3 py-2',
          'text-cream font-body text-sm cursor-pointer',
          'focus:outline-none focus:border-gold-soft focus:ring-1 focus:ring-gold-soft/40',
          className,
        )}
        {...rest}
      >
        {children}
      </select>
    )
  }
)

/* Label */
export function Label({ className, children, ...rest }: HTMLAttributes<HTMLLabelElement>) {
  return (
    <label
      className={cn('eyebrow block mb-1.5', className)}
      {...rest}
    >
      {children}
    </label>
  )
}

/* Section header */
export function SectionHeader({ eyebrow, title, description, action }: {
  eyebrow?: string
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="flex items-end justify-between gap-4 mb-6 pb-4 border-b border-bronze/20">
      <div>
        {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
        <h1 className="font-heading text-3xl text-cream tracking-tight">{title}</h1>
        {description && <p className="font-body text-cream/65 text-sm mt-1.5 max-w-2xl">{description}</p>}
      </div>
      {action && <div className="flex items-center gap-2 shrink-0">{action}</div>}
    </div>
  )
}

/* Skeleton */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div className={cn('rounded-md bg-cream/5 shimmer', className)} />
  )
}

/* Empty state */
export function Empty({ icon, title, description, action }: {
  icon?: ReactNode
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      {icon && <div className="text-bronze/60 mb-4">{icon}</div>}
      <h3 className="font-heading text-xl text-cream mb-2">{title}</h3>
      {description && <p className="text-sm text-cream/55 max-w-md mb-6">{description}</p>}
      {action}
    </div>
  )
}
